import { Modal, Button, Form, Table, Row, Col } from "react-bootstrap";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const RoleManagementModal = ({ show, onClose, getData, toolData }) => {
  // Only these tool IDs should show the "Edit" checkbox
  // const editAllowedTools = [1, 2, 3]; 
  const [role, setRole] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [saving, setSaving] = useState(false);

  // User selection states
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Options data
  const [roleOptions, setRoleOptions] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);

  // Loading states
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);

  const isAddMode = !toolData; // add mode when no toolData
  const moduleName = (toolData?.moduleName ?? toolData?.tool ?? toolData?.module ?? toolData?.name ?? "").toString();

  const allowedToolIds = (() => {
    if (!toolData) return [];
    if (Array.isArray(toolData.tools) && toolData.tools.length) {
      return toolData.tools.map((t) => Number(t.tool_id ?? t.id ?? t.toolId));
    }
    if (toolData.id || toolData.tool_id) return [Number(toolData.id ?? toolData.tool_id)];
    return [];
  })();

  const visibleTools = isAddMode
    ? toolOptions
    : (allowedToolIds.length
        ? toolOptions.filter((t) => allowedToolIds.includes(t.value))
        : toolOptions.filter((t) => (t.label ?? "").toString().toLowerCase() === moduleName.toLowerCase()));

  // Fetch roles and tools
  useEffect(() => {
    fetchRoles();
    fetchTools();
    if (toolData) {
      setRole(toolData.role_id || "");
      // if toolData has detailed tools list use it, otherwise fallback to single id
      if (Array.isArray(toolData.tools) && toolData.tools.length > 0) {
        setSelectedTools(
          toolData.tools.map((t) => ({
            id: Number(t.id),
            view: !!t.view,
            edit: !!t.edit,
          })),
        );
      } else {
        const selected = toolData.id ? [Number(toolData.id)] : [];
        setSelectedTools(selected.map((id) => ({ id, view: true, edit: false })));
      }
    }

  }, [toolData]);

  // Fetch users by role
  useEffect(() => {
    if (role) {
      fetchUsers(role);
    } else {
      setAllUsers([]);
      setFilteredUsers([]);
      setSelectedUsers([]);
    }
  }, [role]);

  // Search filter
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(allUsers);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredUsers(allUsers.filter((u) => {
        const first = u.first_name?.toLowerCase() ?? "";
        const last = u.last_name?.toLowerCase() ?? "";
        const full = `${first} ${last}`.trim();

        return (
          first.includes(q) ||
          last.includes(q) ||
          full.includes(q)       // ← full name match
        );
      }));
    }
  }, [searchQuery, allUsers]);

const toCapitalCase = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // split camelCase
    .replace(/[_-]/g, " ")              // split snake-case or kebab-case
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};



const fetchRoles = async () => {
  setLoadingRoles(true);

  const excludedRoles = ["guest", "user", "frontpage"];

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/coursebuilder-role-user`
    );

    const filtered = res.data.data
      .filter((r) => !excludedRoles.includes(r.role?.toLowerCase()))
      .map((r) => ({
        ...r,
        role: toCapitalCase(r.role), // Convert to "Capital Case"
      }));

    setRoleOptions(filtered);
  } catch (err) {
    toast.error("Failed to fetch roles");
  } finally {
    setLoadingRoles(false);
  }
};

  const fetchTools = async () => {
    setLoadingTools(true);
      try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/coursebuilder-access`);
      // keep a flag (enabled) if API provides a property like is_active / enabled / on / active
      const tools = res.data.data.map((tool) => ({
        value: Number(tool.id),
        label: tool.tool,
        enabled: tool.is_active ?? tool.enabled ?? tool.on ?? tool.active ?? false,
      }));
      setToolOptions(tools);
    } catch (err) {
      toast.error("Failed to fetch tools");
      setToolOptions([]);
    } finally {
      setLoadingTools(false);
    }
  };

  const fetchUsers = async (roleId) => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/coursebuilder-role-user`, {
        params: { role_id: roleId },
      });

      const users = res.data.data || [];
      setAllUsers(users);
      setFilteredUsers(users);
    } catch (err) {
      toast.error("Failed to fetch users");
      setAllUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // const handleSelectAllUsers = (e) => {
  //   if (e.target.checked) {
  //     setSelectedUsers(filteredUsers);
  //   } else {
  //     setSelectedUsers([]);
  //   }
  // }; 

const fetchUserTools = async (userId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/coursebuilder-access`, {
    params: { user_id: userId }
  });

  const tools = res.data?.data ?? [];

  return tools.map((t) => ({
    id: Number(t.tool_id),
    view: !!t.view,
    edit: !!t.edit,
  }));
};

const handleUserToggle = async (user) => {
  const alreadySelected =
    selectedUsers.length === 1 && selectedUsers[0].user_id === user.user_id;

  if (alreadySelected) {
    setSelectedUsers([]);
    setSelectedTools([]);
    return;
  }


  // Fetch tools for that user
  const tools = await fetchUserTools(user.user_id);

  setSelectedUsers([user]);
  setSelectedTools(tools);
};





  const handleSelectAllTools = (e) => {
    if (e.target.checked) {
      setSelectedTools(toolOptions.map((t) => ({ id: t.value, view: true, edit: false })));
    } else {
      setSelectedTools([]);
    }
  };


  const handleToolToggle = (toolValue) => {
    setSelectedTools((prev) => {
      const exists = prev.some((p) => p.id === toolValue);
      if (exists) return prev.filter((p) => p.id !== toolValue);
      return [...prev, { id: toolValue, view: true, edit: false }];
    });
  };

  const handleToolPermissionToggle = (toolValue, permission) => {
    setSelectedTools((prev) =>
      prev.map((t) => {
        if (t.id !== toolValue) return t;
        return { ...t, [permission]: !t[permission] };
      }),
    );
  };

  const handleSubmit = async () => {
    if (!role) return toast.warning("Please select a role");
    if (selectedUsers.length === 0) return toast.warning("Please select users");
    if (selectedTools.length === 0) return toast.warning("Please select tools");

    setSaving(true);

    const access_data = selectedUsers.map((user) => ({
      username: user.username || "",
      user_id: Number(user.user_id),
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      tools: selectedTools.map((t) => ({
        tool_id: Number(t.id),
        edit: t.edit ? 1 : 0,
        view: t.view ? 1 : 0,
      })),
    }));

    const payload = {
      role_id: Number(role),
      access_data,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/coursebuilder-access`,
        payload,
      );

      toast.success("Saved successfully");
      getData && getData();
      onClose();
    } catch (err) {
      console.error("POST error:", err);
      toast.error("Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      style={{ zIndex: 10000 }}
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>{toolData ? "Edit" : "Add"} Role Management</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
        <Form>
          {/* ROLE */}
          <Form.Group className="mb-3">
            <Form.Label>
              Role <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={saving || loadingRoles}
            >
              <option value="">{loadingRoles ? "Loading roles..." : "Select role"}</option>

              {roleOptions.map((r) => (
                <option key={r.role_id} value={r.role_id}>
                  {r.role}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* USERS */}
          <Form.Group className="mb-3">
            <Form.Label>
              User List <span className="text-danger">*</span>
            </Form.Label>

            <Form.Control
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!role || loadingUsers}
              className="mb-2"
            />

            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                maxHeight: "250px",
                overflowY: "auto",
              }}
            >
              {loadingUsers ? (
                <div className="text-center py-4">Loading users...</div>
              ) : !role ? (
                <div className="text-center py-5 text-muted">Select a role first</div>
              ) : (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th width="50" aria-hidden="true" />{/* */}
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <tr
                          key={user.user_id}
                          onClick={() => handleUserToggle(user)}
                          style={{ cursor: "pointer" }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              checked={selectedUsers.length === 1 && selectedUsers[0].user_id === user.user_id}
                              onChange={() => handleUserToggle(user)}
                            />
                            </td>{/* */}
                            <td>
                              {`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()}
                            </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center py-3">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </Form.Group>

          {/* TOOLS */}
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label>
                {isAddMode ? "Permissions" : "Permission"} <span className="text-danger">*</span>
              </Form.Label>
            {isAddMode && (
              <div className="d-flex align-items-center gap-2">
                <span>Select All</span>
                <Form.Check
                  type="switch"
                  checked={
                    toolOptions.length > 0 &&
                    toolOptions.every((t) => selectedTools.some((s) => s.id === t.value))
                  }
                  onChange={handleSelectAllTools}
                />
              </div>
            )}
            </div>

            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "15px",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {loadingTools ? (
                <div className="text-center py-4">Loading tools...</div>
              ) : visibleTools.length > 0 ? (
                <Row>
                  {visibleTools.map((tool) => (
                    <Col md={6} key={tool.value} className="mb-3">
                      <div
                        style={{ border: "1px solid #ddd", padding: "12px", borderRadius: "4px" }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>{tool.label}</strong>
                          <Form.Check
                            type="switch"
                            checked={selectedTools.some((s) => s.id === tool.value)}
                            onChange={() => handleToolToggle(tool.value)}
                          />
                        </div>
                        {selectedTools.some((s) => s.id === tool.value) && (
                          <div className="mt-2 d-flex gap-3 align-items-center">
                            <Form.Check
                              type="checkbox"
                              label="View"
                              checked={selectedTools.find((s) => s.id === tool.value)?.view ?? true}
                              onChange={() => handleToolPermissionToggle(tool.value, "view")}
                            />
                            {/* {editAllowedTools.includes(tool.value) && ( */}
                            <Form.Check
                              type="checkbox"
                              label="Edit"
                              checked={selectedTools.find((s) => s.id === tool.value)?.edit ?? false}
                              onChange={() => handleToolPermissionToggle(tool.value, "edit")}
                            />
                            {/* )} */}
                          </div>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center py-3 text-muted">No tools available</div>
              )}
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Processing..." : "Confirm"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoleManagementModal;
